import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TableConfigService } from '@core/services/table/table-config.service';
import { resolveHeaderSortDirectionToIconName } from '@shared/components/table-implementation/bcl-table-header/components/table-header-icon-resolver/table-header-icon-resolver.component';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { map } from 'rxjs';
import { MatChipsModule } from '@angular/material/chips';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { SORT_DIRECTIONS } from '@shared/components/table-implementation/types/sorting-directions';
import { TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faArrowDown, faArrowUp, faThumbTack, faXmark, IconName } from '@fortawesome/pro-light-svg-icons';
// eslint-disable-next-line @tseslint/no-unused-vars
import { OverviewHeaderComponent } from '../../overview-header.component';
import { getTableColumnsSortedByFields, TableColumn } from '@shared/components/table-implementation/types/column/header.type';


// eslint-disable-next-line @tseslint/typedef
const COLUMN_CHIP_TYPES = {
  sortColumn: "sortColumn",
  fixColumn: "fixColumn"
} as const;

type ColumnChipType = typeof COLUMN_CHIP_TYPES[keyof typeof COLUMN_CHIP_TYPES];

interface ColumnChip extends Pick<TableColumn, "field" | "translationInfo"> {
  chipIconName?: IconName | undefined;
  headerIconName?: IconName | undefined;
  columnChipType: ColumnChipType;
}

/**
 * Add this Component to {@link OverviewHeaderComponent | app-overview-header} to display the column-chips that represent the provided TableConfigs filter
 *
 * @example
 *
 * ```html
 * <app-overview-header>
 *   <!-- other overview-header child-components... -->
 *   <app-overview-header-column-chips />
 *   <!-- other overview-header child-components... -->
 * </app-overview-header>
 * ```
 */
@Component({
  selector: 'app-overview-header-column-chips',
  standalone: true,
  imports: [ MatChipsModule, IconModule, TranslationPipeModule ],
  templateUrl: './overview-header-column-chips.component.html',
  styleUrl: './overview-header-column-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewHeaderColumnChipsComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _tableConfigService: TableConfigService = inject(TableConfigService);

  private readonly _selectedTableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);

  private readonly _fixChips: Signal<ColumnChip[]> = toSignal(this._tableConfigService.selectedConfig$
    .pipe(
      map((config: TableConfig) => config.columnConfig.columns
        .filter((column: TableColumn) => column.options.isFixed && column.options.isVisible)),
      map((columns: TableColumn[]): ColumnChip[] => columns.map((column: TableColumn) => ({
        field: column.field,
        chipIconName: "thumbtack",
        headerIconName: column.icon,
        translationInfo: column.translationInfo,
        columnChipType: COLUMN_CHIP_TYPES.fixColumn
      } satisfies ColumnChip)))
    ), { initialValue: [] });


  private readonly _optionChips: Signal<ColumnChip[]> = toSignal(this._tableConfigService.selectedConfig$
    .pipe(
      map((config: TableConfig) => getTableColumnsSortedByFields(config.columnConfig.fieldsSortedByOptions, config.columnConfig.columns)
        .filter((column: TableColumn) => column.options.isVisible && column.options.sortDirection !== SORT_DIRECTIONS.NO_SORT)
        .map((column: TableColumn) => {
          const sortDirectionIconName: IconName | undefined = resolveHeaderSortDirectionToIconName(column.options.sortDirection);

          return { ...column,
            chipIconName: sortDirectionIconName,
            headerIconName: column.icon,
            columnChipType: COLUMN_CHIP_TYPES.sortColumn };
        }))
    ), { initialValue: [] });


  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected readonly chips: Signal<ColumnChip[]> = computed(() => [
    ...this._optionChips(),
    ...this._fixChips()
  ]);


  constructor () {
    this._faIconLibrary.addIcons(faArrowUp, faArrowDown, faXmark, faThumbTack);
  }


  protected removeChip (chip: ColumnChip): void {
    if (!this._selectedTableConfig()) return;

    switch (chip.columnChipType) {
      case COLUMN_CHIP_TYPES.sortColumn: {
        this._tableConfigService.changeColumnSortDirection(chip.field, SORT_DIRECTIONS.NO_SORT);

        break;
      }

      case COLUMN_CHIP_TYPES.fixColumn: {
        this._tableConfigService.changeColumnFixed(chip.field, false);

        break;
      }

      default: {
        break;
      }
    }
  }
}
